/* Admin → Incentives (v5.5)
 * Manage canonical GM offer catalog + per-offer overrides:
 *  - mark as included in price
 *  - mark proof required / disabled / finance-only / lease-only
 *  - extend not_stackable_with list
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";

export default function AdminIncentives() {
  const [catalog, setCatalog] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/incentives/catalog");
      setCatalog(data.catalog || []);
      const ov = await api.get("/admin/incentives/overrides");
      const map = {};
      for (const o of ov.data.overrides || []) {
        map[o.id] = o;
      }
      setOverrides(map);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const saveOverride = async (id, patch) => {
    setBusy(true);
    setError("");
    try {
      const existing = overrides[id] || {};
      const body = { ...existing, ...patch, id };
      await api.put(`/admin/incentives/overrides/${id}`, body);
      setSavedId(id);
      setTimeout(() => setSavedId(null), 1500);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteOverride = async (id) => {
    if (!window.confirm("Reset this offer's override?")) return;
    try {
      await api.delete(`/admin/incentives/overrides/${id}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-incentives">
      <header>
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">Pricing</div>
        <h1 className="text-3xl font-black text-[var(--ssc-navy)] mt-1">Incentives & Compatibility</h1>
        <p className="text-[var(--ssc-ink-2)] mt-2 max-w-3xl">
          Canonical GM/private offer catalog. Mark offers as included-in-price,
          require proof, restrict to finance/lease, or extend the not-stackable list.
          The Build-A-Deal rebate selector + VDP enforce these rules.
        </p>
      </header>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2">{error}</div>
      )}

      <section className="rounded-xl border border-[var(--ssc-line)] bg-white p-5 shadow-sm overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="admin-incentives-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--ssc-ink-2)] border-b border-[var(--ssc-line)]">
              <th className="py-2 pr-3">Offer</th>
              <th className="py-2 pr-3">Group</th>
              <th className="py-2 pr-3 text-center">Included</th>
              <th className="py-2 pr-3 text-center">Proof</th>
              <th className="py-2 pr-3 text-center">Finance</th>
              <th className="py-2 pr-3 text-center">Lease</th>
              <th className="py-2 pr-3 text-center">Disabled</th>
              <th className="py-2 pr-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {catalog.map((c) => {
              const ov = overrides[c.id] || {};
              const merged = { ...c, ...ov };
              return (
                <tr key={c.id} className="border-b border-[var(--ssc-line)]/50" data-testid={`admin-incentive-row-${c.id}`}>
                  <td className="py-2 pr-3">
                    <div className="font-bold text-[var(--ssc-ink)]">{merged.name}</div>
                    <div className="text-xs text-[var(--ssc-ink-2)] font-mono">{c.id}</div>
                  </td>
                  <td className="py-2 pr-3 text-[var(--ssc-ink-2)]">{merged.group || "—"}</td>
                  <td className="py-2 pr-3 text-center">
                    <Toggle on={!!merged.included_in_price} onChange={(v) => saveOverride(c.id, { included_in_price: v })} testId={`inc-included-${c.id}`} />
                  </td>
                  <td className="py-2 pr-3 text-center">
                    <Toggle on={!!merged.proof_required} onChange={(v) => saveOverride(c.id, { proof_required: v })} testId={`inc-proof-${c.id}`} />
                  </td>
                  <td className="py-2 pr-3 text-center">
                    <Toggle on={!!merged.finance_only} onChange={(v) => saveOverride(c.id, { finance_only: v })} testId={`inc-finance-${c.id}`} />
                  </td>
                  <td className="py-2 pr-3 text-center">
                    <Toggle on={!!merged.lease_only} onChange={(v) => saveOverride(c.id, { lease_only: v })} testId={`inc-lease-${c.id}`} />
                  </td>
                  <td className="py-2 pr-3 text-center">
                    <Toggle on={!!merged.disabled} onChange={(v) => saveOverride(c.id, { disabled: v })} testId={`inc-disabled-${c.id}`} />
                  </td>
                  <td className="py-2 pr-3">
                    {savedId === c.id && <span className="text-xs text-emerald-700 font-bold">Saved</span>}
                    {ov.id && (
                      <button type="button" onClick={() => deleteOverride(c.id)} className="text-xs text-red-700 hover:underline ml-2" disabled={busy}>
                        Reset
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Toggle({ on, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`w-9 h-5 rounded-full transition-colors relative ${on ? "bg-emerald-500" : "bg-slate-300"}`}
      data-testid={testId}
      aria-pressed={on}
    >
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`} />
    </button>
  );
}
